import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "filterContainer",
    "addFilterButton"
  ]

  static values = {
    availableFields: Array,
    filters: Array,
    logicOperator: String
  }

  connect() {
    this.filtersValue = this.filtersValue || []
    this.logicOperatorValue = this.logicOperatorValue || 'AND'
    this.renderFilters()
  }

  addFilter() {
    const newFilter = {
      id: this.generateFilterId(),
      field: '',
      operator: 'equals',
      value: '',
      logicOperator: this.filtersValue.length > 0 ? this.logicOperatorValue : null
    }

    this.filtersValue = [...this.filtersValue, newFilter]
    this.renderFilters()
    this.notifyFiltersChanged()
  }

  removeFilter(event) {
    const filterId = event.target.dataset.filterId
    this.filtersValue = this.filtersValue.filter(filter => filter.id !== filterId)
    this.renderFilters()
    this.notifyFiltersChanged()
  }

  updateFilter(event) {
    const filterId = event.target.dataset.filterId
    const property = event.target.dataset.property
    const value = event.target.value

    const filterIndex = this.filtersValue.findIndex(filter => filter.id === filterId)
    if (filterIndex !== -1) {
      this.filtersValue[filterIndex] = {
        ...this.filtersValue[filterIndex],
        [property]: value
      }

      // If field changed, reset operator and value
      if (property === 'field') {
        this.filtersValue[filterIndex].operator = this.getDefaultOperatorForField(value)
        this.filtersValue[filterIndex].value = ''
      }

      this.renderFilters()
      this.notifyFiltersChanged()
    }
  }

  updateLogicOperator(event) {
    this.logicOperatorValue = event.target.value
    
    // Update all filters except the first one
    this.filtersValue = this.filtersValue.map((filter, index) => ({
      ...filter,
      logicOperator: index > 0 ? this.logicOperatorValue : null
    }))

    this.renderFilters()
    this.notifyFiltersChanged()
  }

  renderFilters() {
    if (!this.hasFilterContainerTarget) return

    if (this.filtersValue.length === 0) {
      this.filterContainerTarget.innerHTML = '<p class="text-muted">No filters configured</p>'
      return
    }

    const filtersHtml = this.filtersValue.map((filter, index) => 
      this.renderFilter(filter, index)
    ).join('')

    const logicOperatorHtml = this.filtersValue.length > 1 ? this.renderLogicOperatorSelector() : ''

    this.filterContainerTarget.innerHTML = `
      ${logicOperatorHtml}
      <div class="filters-list">
        ${filtersHtml}
      </div>
    `
  }

  renderLogicOperatorSelector() {
    return `
      <div class="mb-3 p-3 bg-light rounded">
        <label class="form-label fw-bold">Filter Logic</label>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" name="logicOperator" id="logicAnd" value="AND" 
                 ${this.logicOperatorValue === 'AND' ? 'checked' : ''}
                 data-action="change->filter-builder#updateLogicOperator">
          <label class="form-check-label" for="logicAnd">
            AND (all conditions must be true)
          </label>
        </div>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" name="logicOperator" id="logicOr" value="OR"
                 ${this.logicOperatorValue === 'OR' ? 'checked' : ''}
                 data-action="change->filter-builder#updateLogicOperator">
          <label class="form-check-label" for="logicOr">
            OR (any condition can be true)
          </label>
        </div>
      </div>
    `
  }

  renderFilter(filter, index) {
    const field = this.getFieldByName(filter.field)
    const operators = this.getOperatorsForField(field)
    const valueControl = this.renderValueControl(filter, field)

    return `
      <div class="filter-item mb-3 p-3 border rounded">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <span class="badge bg-secondary">Filter ${index + 1}</span>
          <button type="button" class="btn btn-sm btn-outline-danger" 
                  data-action="click->filter-builder#removeFilter"
                  data-filter-id="${filter.id}">
            <i class="fas fa-trash"></i>
          </button>
        </div>

        ${index > 0 ? `
          <div class="mb-2">
            <span class="badge bg-primary">${filter.logicOperator}</span>
          </div>
        ` : ''}

        <div class="row">
          <div class="col-md-4">
            <label class="form-label">Field</label>
            <select class="form-select" 
                    data-action="change->filter-builder#updateFilter"
                    data-filter-id="${filter.id}"
                    data-property="field">
              <option value="">Select field...</option>
              ${this.availableFieldsValue.map(field => `
                <option value="${field.name}" ${filter.field === field.name ? 'selected' : ''}>
                  ${field.display_name}
                </option>
              `).join('')}
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Condition</label>
            <select class="form-select" 
                    data-action="change->filter-builder#updateFilter"
                    data-filter-id="${filter.id}"
                    data-property="operator">
              ${operators.map(op => `
                <option value="${op.value}" ${filter.operator === op.value ? 'selected' : ''}>
                  ${op.label}
                </option>
              `).join('')}
            </select>
          </div>
          <div class="col-md-5">
            <label class="form-label">Value</label>
            ${valueControl}
          </div>
        </div>
      </div>
    `
  }

  renderValueControl(filter, field) {
    if (!field) {
      return '<input type="text" class="form-control" disabled placeholder="Select a field first">'
    }

    const baseAttributes = `
      data-action="change->filter-builder#updateFilter"
      data-filter-id="${filter.id}"
      data-property="value"
      value="${filter.value || ''}"
    `

    switch (field.type) {
      case 'date':
        return `<input type="date" class="form-control" ${baseAttributes}>`
      
      case 'datetime':
        return `<input type="datetime-local" class="form-control" ${baseAttributes}>`
      
      case 'number':
      case 'currency':
        return `<input type="number" class="form-control" ${baseAttributes} step="any">`
      
      case 'boolean':
        return `
          <select class="form-select" ${baseAttributes}>
            <option value="">Select...</option>
            <option value="true" ${filter.value === 'true' ? 'selected' : ''}>True</option>
            <option value="false" ${filter.value === 'false' ? 'selected' : ''}>False</option>
          </select>
        `
      
      case 'enum':
        if (field.options && field.options.length > 0) {
          return `
            <select class="form-select" ${baseAttributes}>
              <option value="">Select...</option>
              ${field.options.map(option => `
                <option value="${option.value}" ${filter.value === option.value ? 'selected' : ''}>
                  ${option.label}
                </option>
              `).join('')}
            </select>
          `
        }
        return `<input type="text" class="form-control" ${baseAttributes}>`
      
      default:
        // Handle special operators that need different controls
        if (filter.operator === 'in' || filter.operator === 'not_in') {
          return `
            <input type="text" class="form-control" ${baseAttributes} 
                   placeholder="Enter values separated by commas">
            <small class="form-text text-muted">Separate multiple values with commas</small>
          `
        } else if (filter.operator === 'between') {
          const values = filter.value ? filter.value.split(',') : ['', '']
          return `
            <div class="input-group">
              <input type="text" class="form-control" 
                     data-action="change->filter-builder#updateBetweenValue"
                     data-filter-id="${filter.id}"
                     data-position="start"
                     value="${values[0] || ''}"
                     placeholder="From">
              <span class="input-group-text">to</span>
              <input type="text" class="form-control" 
                     data-action="change->filter-builder#updateBetweenValue"
                     data-filter-id="${filter.id}"
                     data-position="end"
                     value="${values[1] || ''}"
                     placeholder="To">
            </div>
          `
        }
        return `<input type="text" class="form-control" ${baseAttributes}>`
    }
  }

  updateBetweenValue(event) {
    const filterId = event.target.dataset.filterId
    const position = event.target.dataset.position
    const value = event.target.value

    const filterIndex = this.filtersValue.findIndex(filter => filter.id === filterId)
    if (filterIndex !== -1) {
      const currentValue = this.filtersValue[filterIndex].value || ','
      const values = currentValue.split(',')
      
      if (position === 'start') {
        values[0] = value
      } else {
        values[1] = value
      }

      this.filtersValue[filterIndex].value = values.join(',')
      this.notifyFiltersChanged()
    }
  }

  getFieldByName(fieldName) {
    return this.availableFieldsValue.find(field => field.name === fieldName)
  }

  getDefaultOperatorForField(fieldName) {
    const field = this.getFieldByName(fieldName)
    if (!field) return 'equals'

    switch (field.type) {
      case 'text':
        return 'contains'
      case 'number':
      case 'currency':
      case 'date':
      case 'datetime':
        return 'equals'
      case 'boolean':
        return 'equals'
      default:
        return 'equals'
    }
  }

  getOperatorsForField(field) {
    if (!field) {
      return [{ value: 'equals', label: 'Equals' }]
    }

    const baseOperators = [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Does not equal' },
      { value: 'is_null', label: 'Is empty' },
      { value: 'is_not_null', label: 'Is not empty' }
    ]

    switch (field.type) {
      case 'text':
        return [
          ...baseOperators,
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does not contain' },
          { value: 'starts_with', label: 'Starts with' },
          { value: 'ends_with', label: 'Ends with' },
          { value: 'in', label: 'Is one of' },
          { value: 'not_in', label: 'Is not one of' }
        ]
      
      case 'number':
      case 'currency':
      case 'date':
      case 'datetime':
        return [
          ...baseOperators,
          { value: 'greater_than', label: 'Greater than' },
          { value: 'greater_than_or_equal', label: 'Greater than or equal' },
          { value: 'less_than', label: 'Less than' },
          { value: 'less_than_or_equal', label: 'Less than or equal' },
          { value: 'between', label: 'Between' },
          { value: 'in', label: 'Is one of' },
          { value: 'not_in', label: 'Is not one of' }
        ]
      
      case 'boolean':
        return [
          { value: 'equals', label: 'Is' },
          { value: 'not_equals', label: 'Is not' }
        ]
      
      default:
        return baseOperators
    }
  }

  generateFilterId() {
    return 'filter_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  validateFilters() {
    const errors = []

    this.filtersValue.forEach((filter, index) => {
      if (!filter.field) {
        errors.push(`Filter ${index + 1}: Please select a field`)
      }
      
      if (!filter.operator) {
        errors.push(`Filter ${index + 1}: Please select a condition`)
      }
      
      if (filter.operator !== 'is_null' && filter.operator !== 'is_not_null' && !filter.value) {
        errors.push(`Filter ${index + 1}: Please enter a value`)
      }

      // Validate between operator
      if (filter.operator === 'between' && filter.value) {
        const values = filter.value.split(',')
        if (values.length !== 2 || !values[0].trim() || !values[1].trim()) {
          errors.push(`Filter ${index + 1}: Between condition requires both start and end values`)
        }
      }
    })

    return errors
  }

  getFilters() {
    return this.filtersValue
  }

  setAvailableFields(fields) {
    this.availableFieldsValue = fields
    this.renderFilters()
  }

  clearFilters() {
    this.filtersValue = []
    this.renderFilters()
    this.notifyFiltersChanged()
  }

  notifyFiltersChanged() {
    const event = new CustomEvent('filters-changed', {
      detail: {
        filters: this.filtersValue,
        logicOperator: this.logicOperatorValue,
        isValid: this.validateFilters().length === 0
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }
}